import { useState, useEffect, useCallback } from 'react';
import { useAdmin } from '@/hooks/useAdmin';
import { useSearchParams } from 'react-router-dom';
import {
  Loader2, Eye, Pencil, Pause, Play, Trash2, Mail, History as HistoryIcon,
  Plus, Upload, X, Search, Filter, Image as ImageIcon, Video, Crown, Gift,
} from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BACKEND = process.env.REACT_APP_BACKEND_URL;

const SLOTS = [
  { value: 'hero', label: 'Hero (vrh naslovnice)' },
  { value: 'sidebar', label: 'Sidebar (gore)' },
  { value: 'sidebar_bottom', label: 'Sidebar (dolje)' },
  { value: 'in_article', label: 'Unutar članka' },
  { value: 'article_bottom', label: 'Ispod članka' },
  { value: 'footer', label: 'Footer' },
];
const AD_KINDS = [
  { value: 'banner', label: 'Banner' },
  { value: 'native', label: 'Nativni' },
  { value: 'takeover', label: 'Takeover' },
];
const SCALING = [
  { value: 'cover', label: 'Obreži (cover)' },
  { value: 'contain', label: 'Uklopi s rubovima (contain)' },
  { value: 'stretch', label: 'Razvuci (stretch)' },
];
const STATUS_BADGE = {
  active: 'bg-emerald-500/20 text-emerald-300',
  paused: 'bg-amber-500/20 text-amber-300',
  pending_media: 'bg-blue-500/20 text-blue-300',
  expired: 'bg-zinc-700 text-zinc-300',
  deleted: 'bg-red-500/20 text-red-300',
  replaced: 'bg-fuchsia-500/20 text-fuchsia-300',
};

function fixUrl(u) { if (!u) return ''; return u.startsWith('http') ? u : `${BACKEND}${u}`; }

function PreviewBox({ p }) {
  if (!p?.media_url) {
    return (
      <div className="aspect-video bg-zinc-950 border border-dashed border-zinc-700 rounded flex flex-col items-center justify-center text-zinc-500 text-xs gap-1">
        <ImageIcon size={20} />
        <span>Bez medija</span>
      </div>
    );
  }
  const objectFit = p.scaling_mode === 'stretch' ? 'fill' : (p.scaling_mode === 'contain' ? 'contain' : 'cover');
  return (
    <div className="aspect-video bg-zinc-950 border border-zinc-700 rounded overflow-hidden">
      {p.media_type === 'video' ? (
        <video src={fixUrl(p.media_url)} muted autoPlay loop playsInline className="w-full h-full" style={{ objectFit }} />
      ) : (
        <img src={fixUrl(p.media_url)} alt={p.title || ''} className="w-full h-full" style={{ objectFit }} />
      )}
    </div>
  );
}

export default function AdminPlacementAds() {
  const { authFetch } = useAdmin();
  const [searchParams, setSearchParams] = useSearchParams();
  const path = typeof window !== 'undefined' ? window.location.pathname : '';
  const pathTab = path.endsWith('/free-ads') ? 'free' : (path.endsWith('/purchased-ads') ? 'paid' : null);
  const initialTab = searchParams.get('tab') || pathTab || 'all';
  const [tab, setTab] = useState(initialTab); // 'all' | 'paid' | 'free'
  const [items, setItems] = useState([]);
  const [counts, setCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');
  const [slotFilter, setSlotFilter] = useState('');
  const [q, setQ] = useState('');
  const [previewing, setPreviewing] = useState(null);
  const [editing, setEditing] = useState(null);
  const [historyOpen, setHistoryOpen] = useState(null);
  const [contactOpen, setContactOpen] = useState(null);
  const [creatingFree, setCreatingFree] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    const params = new URLSearchParams();
    if (tab === 'paid') params.set('kind', 'paid');
    if (tab === 'free') params.set('kind', 'free_admin');
    if (statusFilter) params.set('status', statusFilter);
    if (slotFilter) params.set('slot', slotFilter);
    if (q.trim()) params.set('q', q.trim());
    const r = await authFetch(`${API}/admin/placement-ads?${params}`);
    if (r.ok) {
      const d = await r.json();
      setItems(d.items || []);
      setCounts(d.counts || {});
    }
    setLoading(false);
  }, [authFetch, tab, statusFilter, slotFilter, q]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { setSearchParams(tab === 'all' ? {} : { tab }); }, [tab, setSearchParams]);

  async function action(id, kind, opts = {}) {
    const t = toast.loading('Radim…');
    try {
      let r;
      if (kind === 'delete') {
        r = await authFetch(`${API}/admin/placement-ads/${id}`, { method: 'DELETE' });
      } else if (kind === 'pause' || kind === 'resume') {
        const next = kind === 'pause' ? 'paused' : 'active';
        r = await authFetch(`${API}/admin/placement-ads/${id}`, {
          method: 'PUT', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ status: next }),
        });
      } else if (kind === 'patch') {
        r = await authFetch(`${API}/admin/placement-ads/${id}`, {
          method: 'PUT', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(opts),
        });
      }
      if (!r?.ok) throw new Error(`HTTP ${r?.status}`);
      toast.success('Spremljeno', { id: t });
      load();
    } catch (e) {
      toast.error(e.message || 'Greška', { id: t });
    }
  }

  async function backfill() {
    if (!window.confirm('Materijalizirati postojeće Stripe narudžbe u upravljanje oglasima?')) return;
    const t = toast.loading('Materijaliziram…');
    const r = await authFetch(`${API}/admin/placement-ads/backfill-from-orders`, { method: 'POST' });
    const d = await r.json();
    toast.success(`Kreirano ${d.created || 0} novih placementa`, { id: t });
    load();
  }

  const TabBtn = ({ id, label, badge, icon: Icon }) => (
    <button
      onClick={() => setTab(id)}
      data-testid={`placement-tab-${id}`}
      className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm border transition-colors ${
        tab === id ? 'bg-red-600 text-white border-red-500' : 'bg-zinc-900 text-zinc-400 border-zinc-700 hover:text-white'
      }`}
    >
      {Icon && <Icon size={14} />}
      {label}
      {typeof badge === 'number' && (
        <span className="ml-1 text-[10px] px-1.5 py-0.5 rounded-full bg-zinc-700 text-zinc-200">{badge}</span>
      )}
    </button>
  );

  return (
    <div className="p-6 max-w-7xl" data-testid="admin-placement-ads">
      <div className="flex items-center justify-between mb-5">
        <div>
          <h1 className="text-2xl font-semibold">Upravljanje oglasima</h1>
          <p className="text-xs text-zinc-500 mt-1">Plaćeni i besplatni (admin) oglasi, povijest promjena, kontakt vlasnika.</p>
        </div>
        <div className="flex gap-2">
          <button onClick={backfill} data-testid="placement-backfill-btn"
                  className="text-xs px-3 py-1.5 bg-zinc-800 hover:bg-zinc-700 text-zinc-300 rounded">
            Materijaliziraj postojeće
          </button>
          <button onClick={() => setCreatingFree(true)} data-testid="placement-new-free-btn"
                  className="flex items-center gap-1.5 px-3 py-1.5 bg-emerald-600 hover:bg-emerald-500 text-white text-sm rounded font-semibold">
            <Plus size={14} /> Novi besplatni oglas
          </button>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2 mb-4">
        <TabBtn id="all" label="Svi" />
        <TabBtn id="paid" label="Kupljeni" icon={Crown} badge={(counts.paid_active || 0) + (counts.paid_pending || 0) + (counts.paid_paused || 0)} />
        <TabBtn id="free" label="Besplatni (admin)" icon={Gift} badge={(counts.free_active || 0) + (counts.free_replaced || 0)} />
        <span className="ml-auto" />
        <div className="flex items-center gap-1.5 bg-zinc-900 border border-zinc-700 rounded px-2">
          <Search size={13} className="text-zinc-500" />
          <input value={q} onChange={e => setQ(e.target.value)} placeholder="Pretraži…"
                 data-testid="placement-search"
                 className="bg-transparent px-1 py-1.5 text-sm text-zinc-200 outline-none w-44" />
        </div>
        <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} data-testid="placement-status-filter"
                className="bg-zinc-900 border border-zinc-700 rounded px-2 py-1.5 text-sm text-zinc-300">
          <option value="">Svi statusi</option>
          <option value="active">Aktivni</option>
          <option value="paused">Pauzirani</option>
          <option value="pending_media">Čekaju medij</option>
          <option value="expired">Istekli</option>
          <option value="replaced">Zamijenjeni</option>
        </select>
        <select value={slotFilter} onChange={e => setSlotFilter(e.target.value)} data-testid="placement-slot-filter"
                className="bg-zinc-900 border border-zinc-700 rounded px-2 py-1.5 text-sm text-zinc-300">
          <option value="">Sve pozicije</option>
          {SLOTS.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
        </select>
      </div>

      {loading ? (
        <div className="flex justify-center py-12"><Loader2 className="animate-spin text-zinc-500" /></div>
      ) : items.length === 0 ? (
        <div className="text-center py-16 bg-zinc-900 border border-zinc-800 rounded">
          <Filter size={28} className="mx-auto text-zinc-700 mb-3" />
          <p className="text-zinc-500 text-sm">Nema oglasa za ove filtere.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {items.map(p => (
            <div key={p.id} data-testid={`placement-row-${p.id}`}
                 className="bg-zinc-900 border border-zinc-800 rounded p-3 hover:border-zinc-600">
              <PreviewBox p={p} />
              <div className="mt-3 flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <div className="flex items-center gap-1.5 mb-0.5">
                    {p.kind === 'paid' ? <Crown size={12} className="text-amber-400" /> : <Gift size={12} className="text-emerald-400" />}
                    <span className="text-[10px] uppercase tracking-wider text-zinc-500">{p.kind === 'paid' ? 'Plaćeni' : 'Besplatni'}</span>
                    {p.media_type === 'video' && <Video size={11} className="text-blue-400" title="Video" />}
                  </div>
                  <p className="text-sm font-semibold text-zinc-100 truncate" title={p.title}>{p.title || '(bez naslova)'}</p>
                  <p className="text-xs text-zinc-500 truncate">{p.owner_email || '—'}</p>
                </div>
                <span className={`text-[10px] px-1.5 py-0.5 rounded shrink-0 ${STATUS_BADGE[p.status] || 'bg-zinc-700 text-zinc-300'}`}>
                  {p.status}
                </span>
              </div>
              <div className="mt-2 grid grid-cols-2 gap-1 text-[10px] text-zinc-400">
                <span>Slot: <strong className="text-zinc-200">{p.slot}</strong></span>
                <span>Format: <strong className="text-zinc-200">{p.ad_kind}</strong></span>
                {p.end_at && <span className="col-span-2">Do: {new Date(p.end_at).toLocaleDateString('hr')}</span>}
                {p.amount_eur > 0 && <span>{p.amount_eur.toFixed(2)} €</span>}
                {p.impressions_purchased > 0 && <span>{p.impressions_purchased.toLocaleString('hr')} prik.</span>}
              </div>
              <div className="mt-3 flex flex-wrap gap-1">
                <button onClick={() => setPreviewing(p)} data-testid={`btn-preview-${p.id}`} title="Pregled"
                        className="p-1.5 bg-zinc-800 hover:bg-zinc-700 rounded text-zinc-300"><Eye size={12} /></button>
                <button onClick={() => setEditing(p)} data-testid={`btn-edit-${p.id}`} title="Uredi"
                        className="p-1.5 bg-zinc-800 hover:bg-zinc-700 rounded text-zinc-300"><Pencil size={12} /></button>
                {p.status === 'active' && (
                  <button onClick={() => action(p.id, 'pause')} data-testid={`btn-pause-${p.id}`} title="Pauziraj"
                          className="p-1.5 bg-zinc-800 hover:bg-amber-700 rounded text-amber-300"><Pause size={12} /></button>
                )}
                {p.status === 'paused' && (
                  <button onClick={() => action(p.id, 'resume')} data-testid={`btn-resume-${p.id}`} title="Nastavi"
                          className="p-1.5 bg-zinc-800 hover:bg-emerald-700 rounded text-emerald-300"><Play size={12} /></button>
                )}
                <button onClick={() => setHistoryOpen(p)} data-testid={`btn-history-${p.id}`} title="Povijest"
                        className="p-1.5 bg-zinc-800 hover:bg-zinc-700 rounded text-zinc-300"><HistoryIcon size={12} /></button>
                {p.kind === 'paid' && p.owner_email && (
                  <button onClick={() => setContactOpen(p)} data-testid={`btn-contact-${p.id}`} title="Kontakt korisnika"
                          className="p-1.5 bg-zinc-800 hover:bg-blue-700 rounded text-blue-300"><Mail size={12} /></button>
                )}
                <button onClick={() => window.confirm('Obrisati oglas?') && action(p.id, 'delete')} data-testid={`btn-delete-${p.id}`} title="Obriši"
                        className="p-1.5 bg-zinc-800 hover:bg-red-700 rounded text-red-300 ml-auto"><Trash2 size={12} /></button>
              </div>
            </div>
          ))}
        </div>
      )}

      {previewing && <PreviewModal p={previewing} onClose={() => setPreviewing(null)} />}
      {editing && (
        <EditModal
          p={editing} authFetch={authFetch}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}
      {historyOpen && <HistoryModal p={historyOpen} authFetch={authFetch} onClose={() => setHistoryOpen(null)} />}
      {contactOpen && <ContactModal p={contactOpen} authFetch={authFetch} onClose={() => setContactOpen(null)} />}
      {creatingFree && (
        <CreateFreeModal authFetch={authFetch} onClose={() => setCreatingFree(false)} onCreated={() => { setCreatingFree(false); setTab('free'); load(); }} />
      )}
    </div>
  );
}

function Modal({ children, onClose, title, size = 'lg' }) {
  return (
    <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div onClick={e => e.stopPropagation()}
           className={`bg-zinc-900 border border-zinc-700 rounded-lg w-full ${size === 'lg' ? 'max-w-3xl' : 'max-w-xl'} max-h-[90vh] overflow-auto`}>
        <div className="flex items-center justify-between p-4 border-b border-zinc-800">
          <h2 className="text-sm font-semibold text-zinc-100">{title}</h2>
          <button onClick={onClose} className="text-zinc-500 hover:text-white"><X size={16} /></button>
        </div>
        {children}
      </div>
    </div>
  );
}

function PreviewModal({ p, onClose }) {
  return (
    <Modal title={`Pregled — ${p.title || p.id.slice(0, 8)}`} onClose={onClose}>
      <div className="p-5 space-y-4">
        <PreviewBox p={p} />
        <div className="grid grid-cols-2 gap-2 text-xs text-zinc-300">
          <div><span className="text-zinc-500">Slot:</span> {p.slot}</div>
          <div><span className="text-zinc-500">Format:</span> {p.ad_kind}</div>
          <div><span className="text-zinc-500">Skaliranje:</span> {p.scaling_mode}</div>
          <div><span className="text-zinc-500">Status:</span> {p.status}</div>
          {p.target_url && <div className="col-span-2 truncate"><span className="text-zinc-500">Cilj:</span> <a className="text-blue-400 hover:underline" href={p.target_url} target="_blank" rel="noreferrer">{p.target_url}</a></div>}
          {p.body && <div className="col-span-2"><span className="text-zinc-500">Tekst:</span> {p.body}</div>}
        </div>
      </div>
    </Modal>
  );
}

function EditModal({ p, authFetch, onClose, onSaved }) {
  const [f, setF] = useState({ ...p });
  const [saving, setSaving] = useState(false);
  async function save() {
    setSaving(true);
    const r = await authFetch(`${API}/admin/placement-ads/${p.id}`, {
      method: 'PUT', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        title: f.title, body: f.body, target_url: f.target_url,
        slot: f.slot, ad_kind: f.ad_kind, scaling_mode: f.scaling_mode,
        end_at: f.end_at, status: f.status,
      }),
    });
    setSaving(false);
    if (r.ok) { toast.success('Spremljeno'); onSaved(); } else { toast.error('Greška pri spremanju'); }
  }
  return (
    <Modal title={`Uredi — ${p.title || p.id.slice(0, 8)}`} onClose={onClose}>
      <div className="p-5 space-y-3 text-sm">
        <Field label="Naslov"><input value={f.title || ''} onChange={e => setF({ ...f, title: e.target.value })} className="inp" data-testid="edit-title" /></Field>
        <Field label="Tekst"><textarea value={f.body || ''} onChange={e => setF({ ...f, body: e.target.value })} rows={2} className="inp" data-testid="edit-body" /></Field>
        <Field label="Cilj URL"><input value={f.target_url || ''} onChange={e => setF({ ...f, target_url: e.target.value })} className="inp" data-testid="edit-target" /></Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Slot">
            <select value={f.slot || ''} onChange={e => setF({ ...f, slot: e.target.value })} className="inp" data-testid="edit-slot">
              {SLOTS.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
            </select>
          </Field>
          <Field label="Format">
            <select value={f.ad_kind || ''} onChange={e => setF({ ...f, ad_kind: e.target.value })} className="inp" data-testid="edit-ad-kind">
              {AD_KINDS.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
            </select>
          </Field>
          <Field label="Skaliranje">
            <select value={f.scaling_mode || 'cover'} onChange={e => setF({ ...f, scaling_mode: e.target.value })} className="inp" data-testid="edit-scaling">
              {SCALING.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
            </select>
          </Field>
          <Field label="Status">
            <select value={f.status || ''} onChange={e => setF({ ...f, status: e.target.value })} className="inp" data-testid="edit-status">
              <option value="active">active</option>
              <option value="paused">paused</option>
              <option value="expired">expired</option>
              <option value="pending_media">pending_media</option>
            </select>
          </Field>
        </div>
        <Field label="Datum isteka (ISO ili prazno)">
          <input value={f.end_at || ''} onChange={e => setF({ ...f, end_at: e.target.value })}
                 placeholder="2026-12-31T23:59:00+00:00" className="inp" data-testid="edit-end-at" />
        </Field>
        <div className="flex justify-end gap-2 pt-2">
          <button onClick={onClose} className="px-3 py-1.5 text-xs text-zinc-400 hover:text-white">Odustani</button>
          <button onClick={save} disabled={saving} data-testid="edit-save-btn"
                  className="px-4 py-1.5 bg-red-600 hover:bg-red-700 disabled:opacity-50 text-white text-xs font-bold rounded">
            {saving ? 'Spremam…' : 'Spremi'}
          </button>
        </div>
        <style>{`.inp{width:100%;background:#0a0a0a;border:1px solid #3f3f46;border-radius:6px;padding:6px 10px;color:#e4e4e7;outline:none}`}</style>
      </div>
    </Modal>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="block text-[10px] uppercase tracking-wider text-zinc-500 mb-1">{label}</label>
      {children}
    </div>
  );
}

function HistoryModal({ p, authFetch, onClose }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    authFetch(`${API}/admin/placement-ads/${p.id}/history`).then(r => r.json()).then(d => {
      setRows(d.history || []); setLoading(false);
    });
  }, [p.id, authFetch]);
  return (
    <Modal title={`Povijest — ${p.title || p.id.slice(0, 8)}`} onClose={onClose}>
      <div className="p-5">
        {loading ? <Loader2 className="animate-spin mx-auto text-zinc-500" /> : rows.length === 0 ? (
          <p className="text-center text-zinc-500 text-sm py-6">Nema zapisa.</p>
        ) : (
          <ul className="space-y-2" data-testid="history-list">
            {rows.map(r => (
              <li key={r.id} className="border border-zinc-800 rounded p-2 bg-zinc-950 text-xs">
                <div className="flex justify-between text-zinc-400">
                  <span><strong className="text-zinc-200">{r.action}</strong> · {r.by_kind} · {r.by_email}</span>
                  <span>{new Date(r.created_at).toLocaleString('hr')}</span>
                </div>
                {r.notes && <p className="text-zinc-300 mt-1">{r.notes}</p>}
                {r.diff && Object.keys(r.diff).length > 0 && (
                  <ul className="mt-1 text-[10px] text-zinc-500 space-y-0.5">
                    {Object.entries(r.diff).map(([k, v]) => (
                      <li key={k}><strong>{k}:</strong> {String(v[0] ?? '∅')} → {String(v[1] ?? '∅')}</li>
                    ))}
                  </ul>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>
    </Modal>
  );
}

function ContactModal({ p, authFetch, onClose }) {
  const [subject, setSubject] = useState(`Vaš oglas — ${p.title || ''}`);
  const [body, setBody] = useState('Poštovani,\n\n');
  const [sending, setSending] = useState(false);
  async function send() {
    if (!subject.trim() || !body.trim()) return;
    setSending(true);
    const r = await authFetch(`${API}/admin/placement-ads/${p.id}/contact-user`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ subject, body }),
    });
    setSending(false);
    if (r.ok) {
      const d = await r.json();
      toast.success('Poruka poslana u inbox + mailto otvoren');
      if (d.mailto) window.open(d.mailto, '_blank');
      onClose();
    } else { toast.error('Greška pri slanju'); }
  }
  return (
    <Modal title={`Kontakt — ${p.owner_email}`} onClose={onClose} size="md">
      <div className="p-5 space-y-3 text-sm">
        <Field label="Predmet"><input value={subject} onChange={e => setSubject(e.target.value)} className="inp" data-testid="contact-subject" /></Field>
        <Field label="Poruka"><textarea value={body} onChange={e => setBody(e.target.value)} rows={6} className="inp" data-testid="contact-body" /></Field>
        <div className="flex justify-end gap-2 pt-2">
          <button onClick={onClose} className="px-3 py-1.5 text-xs text-zinc-400 hover:text-white">Odustani</button>
          <button onClick={send} disabled={sending} data-testid="contact-send-btn"
                  className="flex items-center gap-1.5 px-4 py-1.5 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white text-xs font-bold rounded">
            <Mail size={12} /> {sending ? 'Šaljem…' : 'Pošalji'}
          </button>
        </div>
        <style>{`.inp{width:100%;background:#0a0a0a;border:1px solid #3f3f46;border-radius:6px;padding:6px 10px;color:#e4e4e7;outline:none}`}</style>
      </div>
    </Modal>
  );
}

function CreateFreeModal({ authFetch, onClose, onCreated }) {
  const [f, setF] = useState({
    slot: 'hero', ad_kind: 'banner', target_url: '', title: '', body: '',
    scaling_mode: 'cover', media_url: '', media_type: null, end_at: '',
  });
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);

  async function uploadMedia(file) {
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append('file', file);
    fd.append('scaling_mode', f.scaling_mode || 'cover');
    // Step 1: create the row first so we have an id; uploads happen via the admin free-ads media endpoint
    let id = f._id;
    if (!id) {
      const r0 = await authFetch(`${API}/admin/free-ads`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ slot: f.slot, ad_kind: f.ad_kind, title: f.title, body: f.body, target_url: f.target_url, scaling_mode: f.scaling_mode }),
      });
      const d0 = await r0.json();
      id = d0.id; setF({ ...f, _id: id });
    }
    const r = await authFetch(`${API}/admin/free-ads/${id}/media`, { method: 'POST', body: fd });
    setUploading(false);
    if (r.ok) {
      const d = await r.json();
      setF({ ...f, _id: id, media_url: d.media_url, media_type: d.media_type });
      toast.success('Medij učitan');
    } else { toast.error('Greška pri uploadu'); }
  }

  async function save() {
    setSaving(true);
    let id = f._id;
    if (!id) {
      const r0 = await authFetch(`${API}/admin/free-ads`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          slot: f.slot, ad_kind: f.ad_kind, title: f.title, body: f.body,
          target_url: f.target_url, scaling_mode: f.scaling_mode,
          media_url: f.media_url, media_type: f.media_type, end_at: f.end_at || null,
        }),
      });
      if (!r0.ok) { setSaving(false); toast.error('Greška'); return; }
    } else {
      // Patch the existing record with text + end_at via admin update.
      await authFetch(`${API}/admin/placement-ads/${id}`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          title: f.title, body: f.body, target_url: f.target_url,
          slot: f.slot, ad_kind: f.ad_kind, end_at: f.end_at || null,
        }),
      });
    }
    setSaving(false);
    toast.success('Spremljeno');
    onCreated();
  }

  return (
    <Modal title="Novi besplatni oglas (admin)" onClose={onClose}>
      <div className="p-5 grid md:grid-cols-2 gap-4 text-sm">
        <div className="space-y-3">
          <Field label="Slot">
            <select value={f.slot} onChange={e => setF({ ...f, slot: e.target.value })} className="inp" data-testid="free-slot">
              {SLOTS.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
            </select>
          </Field>
          <Field label="Format">
            <select value={f.ad_kind} onChange={e => setF({ ...f, ad_kind: e.target.value })} className="inp" data-testid="free-kind">
              {AD_KINDS.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
            </select>
          </Field>
          <Field label="Naslov"><input value={f.title} onChange={e => setF({ ...f, title: e.target.value })} className="inp" data-testid="free-title" /></Field>
          <Field label="Tekst"><textarea value={f.body} onChange={e => setF({ ...f, body: e.target.value })} rows={2} className="inp" data-testid="free-body" /></Field>
          <Field label="Cilj URL"><input value={f.target_url} onChange={e => setF({ ...f, target_url: e.target.value })} className="inp" data-testid="free-target" /></Field>
          <Field label="Datum isteka (opcionalno)">
            <input value={f.end_at} onChange={e => setF({ ...f, end_at: e.target.value })} placeholder="2026-12-31T23:59:00+00:00" className="inp" data-testid="free-end-at" />
          </Field>
        </div>
        <div className="space-y-3">
          <Field label="Skaliranje pri neispravnoj veličini">
            <select value={f.scaling_mode} onChange={e => setF({ ...f, scaling_mode: e.target.value })} className="inp" data-testid="free-scaling">
              {SCALING.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
            </select>
          </Field>
          <div>
            <label className="block text-[10px] uppercase tracking-wider text-zinc-500 mb-1">Medij (PNG/JPG/GIF/MP4/WebM/MOV, ≤100 MB)</label>
            <label className="flex items-center justify-center gap-2 bg-zinc-800 hover:bg-zinc-700 border border-dashed border-zinc-600 rounded px-3 py-4 cursor-pointer text-zinc-300 text-xs">
              <Upload size={14} />
              {uploading ? 'Učitavam…' : (f.media_url ? 'Zamijeni medij' : 'Učitaj medij')}
              <input type="file" accept="image/*,video/*" onChange={e => uploadMedia(e.target.files?.[0])} className="hidden" data-testid="free-media-input" />
            </label>
            {f.media_url && (
              <div className="mt-3"><PreviewBox p={{ media_url: f.media_url, media_type: f.media_type, scaling_mode: f.scaling_mode }} /></div>
            )}
          </div>
        </div>
        <div className="md:col-span-2 flex justify-end gap-2 pt-2 border-t border-zinc-800">
          <button onClick={onClose} className="px-3 py-1.5 text-xs text-zinc-400 hover:text-white">Odustani</button>
          <button onClick={save} disabled={saving || !f.title}
                  data-testid="free-save-btn"
                  className="px-4 py-1.5 bg-emerald-600 hover:bg-emerald-500 disabled:opacity-50 text-white text-xs font-bold rounded">
            {saving ? 'Spremam…' : 'Spremi'}
          </button>
        </div>
        <style>{`.inp{width:100%;background:#0a0a0a;border:1px solid #3f3f46;border-radius:6px;padding:6px 10px;color:#e4e4e7;outline:none}`}</style>
      </div>
    </Modal>
  );
}
